import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import apiClient from "@/api/client";

/**
 * Zasady podpowiadania podwykonawców — odpowiednik „Bazy wiedzy PRZ-ARON" z wycen dla
 * zamówień zewnętrznych. Zasada to wolny tekst PL z zakresem (firma / rodzaj zlecenia /
 * przedsięwzięcie / okno dat) i działaniem: wskazówka dla AI albo zasada twarda
 * (nie podpowiadaj / podpowiadaj zawsze), egzekwowana też bez AI. Aktywne zasady trafiają
 * do promptu podpowiedzi firmy (`suggestContractors`) razem z faktami z historii zleceń.
 */

export type KnowledgeScope =
  | ""
  | "wpw"
  | "mdcp"
  | "technical"
  | "cost_estimate"
  | "wody"
  | "standard";

/** Rodzaj zlecenia, dla którego prosimy o podpowiedź (bez pustego „każde"). */
export type SuggestScope = Exclude<KnowledgeScope, "">;

export type KnowledgeEffect = "advise" | "exclude" | "prefer";

export const SCOPE_OPTIONS: { value: KnowledgeScope; label: string }[] = [
  { value: "", label: "Każde zlecenie" },
  { value: "wpw", label: "WPW (negocjator)" },
  { value: "mdcp", label: "MDCP (geodezja)" },
  { value: "technical", label: "Projekt techniczny" },
  { value: "cost_estimate", label: "Kosztorys" },
  { value: "wody", label: "Wody Polskie" },
  { value: "standard", label: "Zwykłe zapotrzebowanie" },
];

export const EFFECT_OPTIONS: { value: KnowledgeEffect; label: string; hint: string }[] = [
  {
    value: "advise",
    label: "Wskazówka dla AI",
    hint: "Model czyta zasadę i sam sprawdza jej warunek na faktach (historia zleceń na projekcie, czasy). Bez AI zasada jest tylko pokazywana jako niezweryfikowana.",
  },
  {
    value: "exclude",
    label: "Nie podpowiadaj",
    hint: "Zasada twarda — firma nigdy nie zostaje zaznaczona wstępnie (także bez AI). Ręczny wybór nadal możliwy.",
  },
  {
    value: "prefer",
    label: "Podpowiadaj zawsze",
    hint: "Zasada twarda — firma zostaje zaznaczona wstępnie zawsze, gdy jest w kręgu (także bez AI).",
  },
];

export interface ContractorKnowledgeNote {
  id: string;
  body: string;
  contractor: string | null;
  contractor_name: string | null;
  project: string | null;
  project_name: string | null;
  scope: KnowledgeScope;
  scope_label: string;
  effect: KnowledgeEffect;
  effect_label: string;
  valid_from: string | null;
  valid_until: string | null;
  is_active: boolean;
  /** Aktywna i w oknie dat — tylko takie zasady działają w tej chwili. */
  in_force: boolean;
  created_by_name: string;
  created_at: string;
  updated_at: string;
}

export interface KnowledgeNotePayload {
  body: string;
  contractor?: string | null;
  project?: string | null;
  scope?: KnowledgeScope;
  effect?: KnowledgeEffect;
  valid_from?: string | null;
  valid_until?: string | null;
  is_active?: boolean;
}

export interface KnowledgeNoteFilters {
  contractor?: string;
  project?: string;
  /** Rodzaj zlecenia — backend dorzuca zasady bez rodzaju; "any" = tylko bez rodzaju. */
  scope?: string;
  effect?: KnowledgeEffect;
  is_active?: boolean;
  is_global?: boolean;
}

const BASE = "/contractors/knowledge-notes/";
export const KNOWLEDGE_NOTES_KEY = ["contractors", "knowledge-notes"] as const;
const SUGGEST_KEY = ["contractors", "suggest"] as const;

async function unwrap<T>(p: Promise<{ data: { data: T } }>): Promise<T> {
  const r = await p;
  return r.data.data;
}

export const listKnowledgeNotes = (filters: KnowledgeNoteFilters = {}) =>
  unwrap<ContractorKnowledgeNote[]>(
    apiClient.get(BASE, { params: { ...filters, page_size: 500 } }),
  );

export const createKnowledgeNote = (payload: KnowledgeNotePayload) =>
  unwrap<ContractorKnowledgeNote>(apiClient.post(BASE, payload));

export const updateKnowledgeNote = (id: string, payload: Partial<KnowledgeNotePayload>) =>
  unwrap<ContractorKnowledgeNote>(apiClient.patch(`${BASE}${id}/`, payload));

export const deleteKnowledgeNote = (id: string) => apiClient.delete(`${BASE}${id}/`);

export function useContractorKnowledgeNotes(filters: KnowledgeNoteFilters = {}, enabled = true) {
  return useQuery({
    queryKey: [...KNOWLEDGE_NOTES_KEY, filters],
    queryFn: () => listKnowledgeNotes(filters),
    enabled,
  });
}

export function useKnowledgeNoteMutations() {
  const qc = useQueryClient();
  // A changed rule changes the suggestion too — refresh any panel already on screen, so a
  // rule switched off stops counting without reopening the form.
  // The rules list is awaited (the mutation stays pending until the row shows the new state,
  // so a switch doesn't flick back); the suggestion is not — its refetch may call the AI.
  const onSuccess = () => {
    qc.invalidateQueries({ queryKey: SUGGEST_KEY });
    return qc.invalidateQueries({ queryKey: KNOWLEDGE_NOTES_KEY });
  };
  return {
    create: useMutation({ mutationFn: createKnowledgeNote, onSuccess }),
    update: useMutation({
      mutationFn: (v: { id: string } & Partial<KnowledgeNotePayload>) => {
        const { id, ...payload } = v;
        return updateKnowledgeNote(id, payload);
      },
      onSuccess,
    }),
    /** Włącz / wyłącz zasadę — osobna mutacja, żeby jej stan (w toku, błąd) nie mieszał się
     *  z edycją treści. Wyłączona zasada zostaje w bazie, ale nie wchodzi do podpowiedzi. */
    toggle: useMutation({
      mutationFn: (v: { id: string; is_active: boolean }) =>
        updateKnowledgeNote(v.id, { is_active: v.is_active }),
      onSuccess,
    }),
    remove: useMutation({ mutationFn: deleteKnowledgeNote, onSuccess }),
  };
}

// ── Podpowiedź firmy do bezpośredniego zlecenia ──────────────────────────────

export interface SuggestedContractorRecentOrder {
  order_number: string;
  kind: string;
  order_type_label: string;
  status: string;
  created_at: string;
  hours_ago: number;
  days_ago: number;
  items: string[];
}

export interface SuggestedContractor {
  id: string;
  name: string;
  city: string;
  orders: number;
  last_order_at: string | null;
  offers: number;
  wins: number;
  rating: number | null;
  ratings_count: number;
  distance_km: number | null;
  project_orders_total: number;
  /** Zlecenia na tym przedsięwzięciu per rodzaj (wpw/mdcp/…), bez anulowanych i odrzuconych. */
  project_orders_by_kind: Record<string, number>;
  /** Cała oś czasu zleceń firmy na tym przedsięwzięciu, najnowsze pierwsze (do 10). */
  project_orders_timeline: SuggestedContractorRecentOrder[];
  /** Typy prac z kartoteki (wpw / surveyor / surveyor_wpw / coordinator / design_reviewer). */
  work_types: string[];
  work_type_labels: string[];
  /** Numery zasad ([Z-n] z `rules`) dotyczących tej firmy. */
  rule_ids: number[];
  hard_rule_ids: number[];
  excluded_by_rule: boolean;
  preferred_by_rule: boolean;
  /** Urlop firmy w oknie składania zamówienia (dziś + 7 dni) — firma ląduje na dole listy
   *  (także wśród zaznaczonych), a `leave_label` („urlop 12.10.2026–20.10.2026") idzie w
   *  nawiasie za nazwą. Nadal może być zaznaczona. */
  on_leave: { id: string; start_date: string; end_date: string; is_ongoing: boolean } | null;
  leave_label: string;
  /** INNE urlopy firmy w terminie realizacji zamówienia (do `deadline` z formularza) —
   *  „planowany urlop od–do"; puste bez terminu albo bez urlopów. Ten sam urlop nie
   *  pojawia się tu i w `on_leave`. */
  planned_leaves: { id: string; start_date: string; end_date: string; is_ongoing: boolean }[];
  planned_leave_label: string;
  /** Wstępne zaznaczenie (AI albo punktacja z historii + twarde zasady) — propozycja. */
  preselected: boolean;
  reason: string;
  /** Tylko scope=wpw: skąd firma weszła do kręgu — typ „Negocjator WPW" w kartotece
   *  i/lub aktywna pozycja WPW w cenniku umowy ramowej; [] = firma spoza kręgu. */
  eligible_via?: ("work_type" | "line_item")[];
}

export interface SuggestionRule {
  index: number;
  id: string;
  body: string;
  scope: KnowledgeScope;
  scope_label: string;
  effect: KnowledgeEffect;
  effect_label: string;
  contractor_id: string | null;
  contractor_name: string | null;
  project_id: string | null;
  label: string;
}

export interface ContractorSuggestion {
  contractors: SuggestedContractor[];
  note: string;
  /** „ai" = wybrał model, „rules" = punktacja z historii (AI niedostępne), „empty" = brak kręgu. */
  source: "ai" | "rules" | "empty";
  /** Treść zasad w grze — tylko dla dyrektora technicznego / CEO; reszta dostaje []. */
  rules: SuggestionRule[];
  /** Ile zasad zadziałało (także gdy `rules` jest ukryte). */
  rules_count?: number;
  now: string;
  scope: SuggestScope;
  eligible_count: number;
}

export interface SuggestPayload {
  scope: SuggestScope;
  project?: string;
  connection?: string;
  /** Zawężenie kręgu do firm z pickera — bez niego krąg wynika z rodzaju zlecenia. */
  candidates?: string[];
  /** Termin realizacji z formularza (yyyy-mm-dd) — włącza „planowany urlop" w wierszach. */
  deadline?: string;
}

export const suggestContractors = (payload: SuggestPayload) =>
  unwrap<ContractorSuggestion>(apiClient.post("/contractors/suggest/", payload));

export function useContractorSuggestion(payload: SuggestPayload | null, enabled = true) {
  return useQuery({
    queryKey: [...SUGGEST_KEY, payload],
    queryFn: () => suggestContractors(payload!),
    enabled: enabled && !!payload,
    retry: false,
  });
}
